import { useState, useEffect, useCallback } from "react";
import { useApp } from "@/context/AppContext";
import { isDevMode } from "@/lib/firebase";
import { requestFcmPermission } from "@/lib/fcm";
import { ArrowRight, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import type { Role } from "@/context/AppContext";
import { VideoExplainer } from "@/components/VideoExplainer";

const STORAGE_KEY = "mb_onboarding_done";
const TIP_SHOWN_PREFIX = "mb_tip_shown_at_";
const TIP_CLOSED_PREFIX = "mb_tip_closed_";
const TIP_TTL_MS = 7 * 24 * 60 * 60 * 1000;

export const ONBOARDING_EVENT = "mombongo:show-onboarding";

export function resetOnboarding() {
  localStorage.removeItem(STORAGE_KEY);
  window.dispatchEvent(new Event(ONBOARDING_EVENT));
}

const SLIDES: Record<Role, { emoji: string; title: string; body: string }[]> = {
  farmer: [
    { emoji: "👋", title: "Bienvenue sur Mombongo", body: "Votre assistant agricole et financier personnel." },
    { emoji: "📦", title: "Vendez vos récoltes", body: "Publiez vos produits sur le marché. Les commerçants vous contactent directement." },
    { emoji: "💰", title: "Obtenez un financement", body: "Demandez un crédit agricole et suivez votre dossier depuis l'app." },
    { emoji: "📊", title: "Suivez votre exploitation", body: "Gérez vos cultures, vos récoltes et vos revenus en un seul endroit." },
  ],
  investor: [
    { emoji: "👋", title: "Bienvenue sur Mombongo", body: "Investissez dans l'agriculture africaine. Simple, sécurisé, rentable." },
    { emoji: "🌿", title: "Choisissez un produit", body: "Parcourez les projets agricoles vérifiés. À partir de 50 USD." },
    { emoji: "💳", title: "Déposez des fonds", body: "Via Airtel Money, Orange Money, M-Pesa ou carte bancaire." },
    { emoji: "📈", title: "Suivez vos rendements", body: "Votre tableau de bord montre vos gains et l'avancement de chaque projet." },
  ],
  agent: [
    { emoji: "👋", title: "Bienvenue sur Mombongo", body: "Vous êtes le lien entre les agriculteurs et les financeurs." },
    { emoji: "🗺️", title: "Vos agriculteurs", body: "Consultez la liste des agriculteurs qui vous sont assignés." },
    { emoji: "📝", title: "Rapports de terrain", body: "Soumettez vos rapports avec photos pour valider les tranches." },
    { emoji: "✅", title: "Validez les jalons", body: "Chaque rapport approuvé débloque un versement pour l'agriculteur." },
  ],
  merchant: [
    { emoji: "👋", title: "Bienvenue sur Mombongo", body: "Achetez des produits agricoles directement auprès des producteurs." },
    { emoji: "🔍", title: "Parcourez le marché", body: "Consultez les offres de milliers d'agriculteurs en DRC." },
    { emoji: "📋", title: "Passez des commandes", body: "Proposez un prix, négociez, et signez un contrat numérique." },
    { emoji: "🚚", title: "Suivez vos livraisons", body: "Confirmez la réception et libérez le paiement sécurisé Mombongo." },
  ],
};

const TIPS: Record<Role, string> = {
  farmer:   "Astuce : publiez votre première annonce sur le Marché pour être visible des commerçants.",
  investor: "Astuce : vérifiez votre identité pour pouvoir déposer et retirer des fonds.",
  agent:    "Astuce : consultez votre liste d'agriculteurs pour soumettre votre premier rapport.",
  merchant: "Astuce : explorez le marché pour trouver des produits frais près de chez vous.",
};

/* ─── Onboarding modal ──────────────────────────────────────────────────────── */

export function OnboardingModal() {
  const { role } = useApp();
  const [visible, setVisible] = useState(false);
  const [slide, setSlide] = useState(0);

  const slides = SLIDES[role] ?? SLIDES.investor;

  const open = useCallback(() => {
    setSlide(0);
    setVisible(true);
  }, []);

  useEffect(() => {
    if (isDevMode()) return;
    if (!localStorage.getItem(STORAGE_KEY)) open();
    const handler = () => open();
    window.addEventListener(ONBOARDING_EVENT, handler);
    return () => window.removeEventListener(ONBOARDING_EVENT, handler);
  }, [open]);

  const dismiss = (requestPush = false) => {
    localStorage.setItem(STORAGE_KEY, "1");
    setVisible(false);
    if (requestPush) requestFcmPermission();
  };

  const isLast = slide === slides.length - 1;
  const current = slides[slide];

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="onboarding-backdrop"
          data-testid="onboarding-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
        >
          <motion.div
            initial={{ scale: 0.92, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.92, opacity: 0 }}
            transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="relative bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden"
          >
            {/* Passer */}
            <button
              data-testid="onboarding-skip"
              onClick={dismiss}
              className="absolute top-4 right-4 text-[12px] text-gray-400 hover:text-gray-600 font-semibold transition z-10"
            >
              Passer
            </button>

            {/* Slide */}
            <div className="px-8 pt-12 pb-4 text-center min-h-[220px] flex flex-col items-center justify-center">
              <div className="text-[64px] leading-none mb-5" aria-hidden="true">{current.emoji}</div>
              <h2 className="font-display font-extrabold text-[20px] text-gray-900 mb-3 text-balance">
                {current.title}
              </h2>
              <p className="text-[14px] text-gray-500 leading-relaxed">{current.body}</p>
              {isLast && (
                <div className="mt-4">
                  <VideoExplainer slug="onboarding" label="Voir comment ça marche" />
                </div>
              )}
            </div>

            {/* Dots */}
            <div className="flex justify-center gap-2 py-4">
              {slides.map((_, i) => (
                <button
                  key={i}
                  data-testid={`onboarding-dot-${i}`}
                  onClick={() => setSlide(i)}
                  className={`rounded-full transition-all duration-200 ${
                    i === slide ? "w-6 h-2 bg-green-700" : "w-2 h-2 bg-gray-200"
                  }`}
                />
              ))}
            </div>

            {/* CTA */}
            <div className="px-6 pb-8">
              {isLast ? (
                <button
                  data-testid="onboarding-start"
                  onClick={() => dismiss(true)}
                  className="w-full h-12 bg-green-700 hover:bg-green-800 text-white rounded-2xl font-display font-bold text-[15px] flex items-center justify-center gap-2 transition active:scale-[0.99]"
                >
                  Commencer <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <button
                  data-testid="onboarding-next"
                  onClick={() => setSlide((s) => s + 1)}
                  className="w-full h-12 bg-green-700 hover:bg-green-800 text-white rounded-2xl font-display font-bold text-[15px] flex items-center justify-center gap-2 transition active:scale-[0.99]"
                >
                  Suivant <ArrowRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* ─── Tip banner ────────────────────────────────────────────────────────────── */

export function TipBanner() {
  const { role } = useApp();
  const shownKey = `${TIP_SHOWN_PREFIX}${role}`;
  const closedKey = `${TIP_CLOSED_PREFIX}${role}`;

  const [visible, setVisible] = useState(() => {
    if (localStorage.getItem(closedKey)) return false;
    const shownAt = localStorage.getItem(shownKey);
    if (!shownAt) {
      localStorage.setItem(shownKey, String(Date.now()));
      return true;
    }
    return Date.now() - Number(shownAt) < TIP_TTL_MS;
  });

  if (!visible) return null;

  const dismiss = () => {
    localStorage.setItem(closedKey, "1");
    setVisible(false);
  };

  return (
    <div
      data-testid="tip-banner"
      className="mx-4 mt-3 mb-1 flex items-start gap-3 bg-green-50 border border-green-200 rounded-xl px-4 py-3 text-[13px] text-green-800"
    >
      <span className="flex-1 leading-snug">💡 {TIPS[role]}</span>
      <button
        data-testid="tip-banner-close"
        onClick={dismiss}
        className="flex-shrink-0 text-green-600 hover:text-green-800 transition mt-0.5"
        aria-label="Fermer"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
